import React, { CSSProperties, useMemo, forwardRef, useRef, useState, useImperativeHandle, useEffect } from 'react'
import { Button as BaseButton } from "baseui/button"
// import { ButtonTypes, CreateButtonStyle } from '../../Definitions/buttonTypes'
import { ButtonTypes, CreateButtonStyle, CreateListStyle, ListStyle_Types } from '../../Definitions'
import { useLocation } from 'react-router'
import { colors, fonts, glyphs, icons, useMediaQuery } from '../../Styles'

export type Button_PropHandle = {
  setActive: (state:boolean) => void,
}

interface Button_Props_overrides {
  baseButton?: CSSProperties
  hoverStyles?: CSSProperties
  startEnhancerStyles?: CSSProperties
  endEnhancerStyles?: CSSProperties
  hoverEnhancerStyles?: CSSProperties
  contentStyles?: CSSProperties
  subContentStyles?: CSSProperties
  hoverSubContentStyles?: CSSProperties
  disabledStyles?: CSSProperties
}

// #region | Button
interface Button_Props {
  content: string,
  subContent?: string,
  startEnhancer?: JSX.Element | string
  endEnhancer?: JSX.Element | string
  buttonType?: ButtonTypes
  activeLoad?: boolean
  hasActiveLoad?: boolean
  disabled?: boolean
  onClick?:()=>void
  overrides?: Button_Props_overrides
}
/**
 * Button
 * @param content The main text displayed on the button.
 * @param subContent Optional secondary text displayed below the main text.
 * @param startEnhancer Optional element displayed at the start of the button.
 * @param endEnhancer Optional element displayed at the end of the button.
 * @param buttonType Determines the visual style of the button.
 * @param activeLoad If true, marks the button as active when loaded.
 * @param hasActiveLoad If true, allows the button to toggle active state.
 * @param disabled If true, button will be unclickable.
 * @param onClick Callback function triggered when the button is clicked.
 * @param overrides Custom styles for different parts of the button.
 * @returns A styled button component.
 */
const _Button:React.ForwardRefRenderFunction<Button_PropHandle, Button_Props> = ({content, subContent, startEnhancer, endEnhancer, buttonType, disabled, onClick, activeLoad = false, hasActiveLoad = false, overrides}, ref) => {
  const { baseStyles, hoverStyles, startEnhancerStyles, endEnhancerStyles, contentStyles, subContentStyles, hoverSubContentStyles, hoverEnhancerStyles, disabledStyles } = useMemo(() => CreateButtonStyle(buttonType, overrides), [buttonType, overrides]);
  const [isActive, setIsActive] = useState<boolean>(activeLoad)
  const [isHovered, setIsHovered] = React.useState(false);
  
  // #region | Handle enhancers based on type
  const enhancerFromType = (enhancer: JSX.Element | string | undefined, enhancerStyles: CSSProperties) => {
    if (!enhancer) return null;
    return typeof enhancer === "string" 
      ? <img alt="enhancer" src={enhancer} style={{ ...enhancerStyles, width: "40px" }} />
      : enhancer;
  }

  const startEnhancerFromType = enhancerFromType(startEnhancer, startEnhancerStyles);
  const endEnhancerFromType = enhancerFromType(endEnhancer, endEnhancerStyles);
  // #endregion

  // #region | Handle active button
  // Method to toggle the component's active state from a parent via ref.
  useImperativeHandle(ref, ()=>({
    setActive(state) {
      if (isActive !== state) setIsActive(state)
    }
  }))

  // Handle style when active
  const activeStyles = ((isActive && hasActiveLoad) || isHovered)
    ? { ...baseStyles, ...hoverStyles }
    : { ...baseStyles }

  const activeEnhancerStyles = ((isActive && hasActiveLoad) || isHovered)
    ? { ...startEnhancerStyles, ...hoverEnhancerStyles }
    : { ...startEnhancerStyles }

  const activeSubcontentStyles = ((isActive && hasActiveLoad) || isHovered)
    ? { ...subContentStyles, ...hoverSubContentStyles }
    : { ...subContentStyles }
  // #endregion

  return (
    <BaseButton
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      $isSelected={true}
      children={
        <div style={{display: "flex", flexDirection: "column", gap: "5px"}}>
          <p style={{...contentStyles}}>{content}</p>
          {!subContent
            ? <></> 
            : <p style={{...activeSubcontentStyles, ...fonts.paragraph}}>{subContent}</p>}
        </div>
      }
      startEnhancer={startEnhancerFromType}
      endEnhancer={endEnhancerFromType}
      disabled={disabled}
      onClick={() => {
        if (onClick) onClick()
        if (hasActiveLoad) setIsActive(true)
      }}
      overrides={{
        EndEnhancer:{ style: () => Object.assign({}, {
          ...endEnhancerStyles
        } as React.CSSProperties )},
        StartEnhancer:{ style: () => Object.assign({}, {
          ...activeEnhancerStyles
        } as React.CSSProperties )},
        BaseButton:{ style: ({$disabled}) => Object.assign({}, {
          ...activeStyles,
          ":disabled": {
            ...activeStyles,
            ...disabledStyles,
            cursor: "default"
          }
        } as React.CSSProperties )},
      }}
    />
  )
}
const Button = forwardRef(_Button)
export default Button
// #endregion

// #region | ButtonList
interface ButtonList_Props {
  contents: string[]
  subContents?: string[]
  startEnhancers?: (JSX.Element|undefined)[]
  endEnhancers?: JSX.Element[]
  buttonType?: ButtonTypes
  hasActiveLoad?: boolean
  disabled?: boolean[]
  onClick?:(Btn_Index:number)=>void
  overrides?: Button_Props_overrides | ((index: number) => Button_Props_overrides);
  listOverrides?: {
    useNavigationControl?:string
    onLoadNumber?:number
    listStyle?: ListStyle_Types
    listGap?:string
    listWidth?: string
    // responsiveList?: boolean
    // responsiveStyle?: CSSProperties
    // responsiveType?: ButtonTypes
    // responsiveEnhancer?: JSX.Element
    // responsiveCloseEnhancer?: JSX.Element
    // responsiveContent?: string
    // responsiveCloseContent?: string
  }
  responsiveOverrides?: {
    isResponsive?: boolean
    buttonType?: ButtonTypes
    openContent?: string
    closeContent?: string
    openEnhancer?: JSX.Element
    closeEnhancer?: JSX.Element
    popoverStyle?: CSSProperties
  }
}
/**
 * ButtonList
 * @param contents Array of main texts for the buttons in the list.
 * @param subContents Optional array of secondary texts for the buttons.
 * @param startEnhancers Optional array of start elements for the buttons.
 * @param endEnhancers Optional array of end elements for the buttons.
 * @param buttonType Determines the visual style of all buttons in the list.
 * @param hasActiveLoad If true, allows buttons in the list to toggle active state.
 * @param disabled  Optional array of booleans that, if true, makes the button unclickable.
 * @param onClick Callback function triggered when a button in the list is clicked.
 * @param overrides Custom styles for individual button parts.
 * @param listOverrides Additional customization for the list layout or navigation.
 * @returns A styled list of buttons with optional separators.
 */
export const ButtonList = ({ contents, subContents, startEnhancers, endEnhancers, buttonType, hasActiveLoad=false, disabled, onClick, overrides, listOverrides, responsiveOverrides }:ButtonList_Props) => {
  let location = useLocation()
  const buttonRefs = useRef<Button_PropHandle[]>([])
  const [isActive, setisActive] = useState<number>(listOverrides&&listOverrides.onLoadNumber?(listOverrides.onLoadNumber>=0?listOverrides.onLoadNumber:0):0)

  const { listDirection } = useMemo(() => CreateListStyle(listOverrides?.listStyle), [listOverrides?.listStyle]);
  const _listGap = listOverrides?.listGap ? { gap: listOverrides.listGap } : { gap: "5px" };
  const _listWidth = listOverrides?.listWidth ? { width: listOverrides.listWidth} : { width: "fit-content"}
  
  const [showList, setShowList] = useState(false);
  const listRef = useRef<HTMLDivElement>(null);
  const buttonListRef = useRef<HTMLDivElement>(null);
  const _resButtonType = responsiveOverrides?.buttonType ? responsiveOverrides.buttonType : ButtonTypes.textButton
  const _resOpenContent = responsiveOverrides?.openContent ? responsiveOverrides.openContent : ""
  const _resCloseContent = responsiveOverrides?.closeContent ? responsiveOverrides.closeContent : ""
  const _resOpenEnhancer = responsiveOverrides?.openEnhancer ? responsiveOverrides.openEnhancer : <>{glyphs.Setting({size: "25px"})}</>
  const _resCloseEnhancer = responsiveOverrides?.closeEnhancer ? responsiveOverrides.closeEnhancer : <>{glyphs.Close({size: "25px"})}</>
  const _resPopoverStyle = responsiveOverrides?.popoverStyle

  const Item_List = useMemo(() => {
    return contents.map((_contents, index) => {
      var _subContents = subContents&&subContents[index] ? subContents[index] : undefined
      var _disabled = disabled&&disabled[index] ? disabled[index] : undefined
      var _startEnhancers = startEnhancers?startEnhancers[index] : undefined
      var _endEnhancers = endEnhancers&&endEnhancers[index] ? endEnhancers[index] : undefined
      const _overrides = typeof overrides === 'function' ? overrides(index) : overrides;

      var view = [(
        <Button key={"List_Input_" + index} 
          ref={(el) => el?buttonRefs.current[index] = el:{}}
          content={_contents} 
          subContent={_subContents}
          activeLoad={isActive===index}
          hasActiveLoad={hasActiveLoad}
          disabled={_disabled}
          buttonType={buttonType}
          endEnhancer={_endEnhancers}
          startEnhancer={_startEnhancers}
          overrides={_overrides}
          onClick={() => {
            if (!listOverrides?.useNavigationControl) {
              setisActive((n) => {
                if (n >= 0 && buttonRefs.current && buttonRefs.current[n]) buttonRefs.current[n].setActive(false)
                return index
              })
            }
            if (responsiveOverrides?.isResponsive) {
              setShowList(false);
            }
            if (onClick) onClick(index)
          }}
        />
      )]
      return view
    })
  }, [contents, subContents, disabled, startEnhancers, endEnhancers, overrides, isActive, hasActiveLoad, buttonType, listOverrides?.useNavigationControl, responsiveOverrides?.isResponsive, onClick])

  // #region | Handle click outside responsive button list
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        listRef.current && 
        !listRef.current.contains(event.target as Node) && 
        buttonListRef.current && 
        !buttonListRef.current.contains(event.target as Node)
      ) {
        setShowList(false);
      }
    };

    if (showList) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showList]);
  // #endregion

  // #region | Handle active button based on userNavigationControl
  useEffect(() => {
    if (listOverrides?.useNavigationControl) {
      if (location.state && location.state[listOverrides.useNavigationControl] !== undefined) {
        var index = location.state[listOverrides.useNavigationControl]
        if (isActive !== index) {
          setisActive((n) => {
            if (buttonRefs.current) {
              if (buttonRefs.current[n]) buttonRefs.current[n].setActive(false)
              if (buttonRefs.current[index]) buttonRefs.current[index].setActive(true)
            }
            return index
          })
        }
      }
      else if (buttonRefs.current && buttonRefs.current[isActive]) buttonRefs.current[isActive].setActive(false)
    }
  }, [isActive, location.state, listOverrides?.useNavigationControl])
  // #endregion

  return (    
    <div ref={buttonListRef} style={{position: "relative", ..._listWidth}}>
      { responsiveOverrides?.isResponsive ? (
        <>
          { showList 
            ? ( <>
              <Button 
                content={_resCloseContent} 
                onClick={() => setShowList(false)}
                buttonType={_resButtonType}
                startEnhancer={<>{_resCloseEnhancer}</>}
              /> 
              <div
                ref={listRef}
                style={{
                  ...listDirection as React.CSSProperties, 
                  ..._listGap,
                  position: "absolute",
                  top: "30px",
                  right: "5px",
                  zIndex: "1000",
                  ..._resPopoverStyle
                }}
              >
                {Item_List}
              </div>
            </> ) 
          : <Button 
              content={_resOpenContent} 
              onClick={() => setShowList(true)}
              buttonType={_resButtonType}
              startEnhancer={<>{_resOpenEnhancer}</>}
            />
          }
        </>
      ) : (
        <div style={{...listDirection as React.CSSProperties, ..._listGap}}>
          {Item_List}
        </div>
      )}
    </div>
  );
}
// #endregion